<script setup lang="ts">
import { ActionButton, StatusTag } from '@taler/ui'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  ledgerName: string
  isDisabled: boolean
  isDisabling: boolean
  disableError: Error | null
  isRestoring: boolean
  restoreError: Error | null
}>()

const emit = defineEmits<{
  disable: []
  restore: []
}>()

const { t } = useI18n()
const confirming = ref(false)

watch(
  () => props.ledgerName,
  () => (confirming.value = false),
)

function confirmDisable(): void {
  confirming.value = false
  emit('disable')
}
</script>

<template>
  <section
    class="flex flex-col gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-block)]"
  >
    <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
      {{ ledgerName }}
    </h2>

    <StatusTag v-if="isDisabled" tone="neutral">{{ t('ledgerLifecycle.statusDisabled') }}</StatusTag>
    <StatusTag v-else tone="ok">{{ t('ledgerLifecycle.statusActive') }}</StatusTag>

    <template v-if="isDisabled">
      <p class="text-[color:var(--color-text-muted)]">{{ t('ledgerLifecycle.disabledBody') }}</p>

      <ActionButton type="button" :disabled="isRestoring" @click="emit('restore')">
        {{ isRestoring ? t('ledgerLifecycle.restoring') : t('ledgerLifecycle.restoreAction') }}
      </ActionButton>

      <StatusTag v-if="restoreError" tone="danger" role="alert">
        {{ t('ledgerLifecycle.restoreError') }}
      </StatusTag>
    </template>

    <template v-else>
      <p class="text-[color:var(--color-text-muted)]">{{ t('ledgerLifecycle.activeBody') }}</p>

      <div v-if="confirming" class="flex flex-col gap-[var(--space-inline)]">
        <p class="font-semibold text-[color:var(--color-text)]">
          {{ t('ledgerLifecycle.disableConfirmTitle', { name: ledgerName }) }}
        </p>
        <p class="text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]">
          {{ t('ledgerLifecycle.disableConfirmBody') }}
        </p>
        <div class="flex gap-[var(--space-inline)]">
          <button
            type="button"
            :disabled="isDisabling"
            class="min-h-[var(--density-target-min)] flex-1 rounded-[var(--radius-control)] bg-[color:var(--color-danger)] px-3 py-2 text-[color:var(--color-text-inverse)] disabled:opacity-60"
            @click="confirmDisable"
          >
            {{ isDisabling ? t('ledgerLifecycle.disabling') : t('ledgerLifecycle.disableConfirmAction') }}
          </button>
          <button
            type="button"
            class="min-h-[var(--density-target-min)] flex-1 rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
            @click="confirming = false"
          >
            {{ t('ledgerLifecycle.disableCancel') }}
          </button>
        </div>
      </div>
      <button
        v-else
        type="button"
        class="min-h-[var(--density-target-min)] self-start rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
        @click="confirming = true"
      >
        {{ t('ledgerLifecycle.disableAction') }}
      </button>

      <StatusTag v-if="disableError" tone="danger" role="alert">
        {{ t('ledgerLifecycle.disableError') }}
      </StatusTag>
    </template>
  </section>
</template>
